/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabMessageService} from '@fabric/components/message';
import {ColDef} from 'ag-grid-community';
import {DISMISS_MSG, VEHICLE_COLDEF_NO_FILTER} from '../../../common/constants';
import {
  VehicleScenarioSelectPopUpComponent
} from '../../../core/components/vehicle-scenario-select/vehicle-scenario-select-pop-up/vehicle-scenario-select-pop-up.component';
import {getDefaultYearRange, range} from '../../../core/helpers/generic-helpers';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {StateService} from '../../../core/state/state.service';
import {MarketShareSegmentReportRequest} from '../../../models/reports/market-share-segment-report-request.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {GlobalScenarioService} from '../../../services/global-scenario-service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';
import {EXPORT_DATA_ERROR} from '../../admin/vehicle-snapshot/vehicle-snapshot.component';

@Component({
    selector: 'app-market-share-sgmnt-rpt',
    templateUrl: './market-share-sgmnt-rpt.component.html',
    styleUrls: ['./market-share-sgmnt-rpt.component.scss'],
    standalone: false
})
export class MarketShareSgmntRptComponent implements OnInit {
  REGION = 'REGION';
  SUBREGION = 'SUBREGION';
  PLNGMKT = 'PLNGMKT';
  ARCH = 'ARCH';
  MKTDIV = 'MKTDIV';
  LBRAND = 'LBRAND';
  PRGMCODE = 'PRGMCODE';
  TOS = 'TOS';

  showSpinner: boolean;
  isIncludeTos: boolean;
  isIncludeCompareScenario: boolean;
  combinePhevIce: boolean;

  selectedScenarioType: string;
  selectedFcorgName: string;
  selectedVehScenRow: any;
  compareScenario: any;
  selectedGlblScenRow: any;
  selectedCompareGlblScenRow: any;

  //Filters
  regionOptions: SelectableOptions = new SelectableOptions();
  subregionOptions: SelectableOptions = new SelectableOptions();
  plngMktOptions: SelectableOptions = new SelectableOptions();
  archOptions: SelectableOptions = new SelectableOptions();
  mktDivOptions: SelectableOptions = new SelectableOptions();
  lbrandOptions: SelectableOptions = new SelectableOptions();
  prgmCodeOptions: SelectableOptions = new SelectableOptions();
  tosOptions: SelectableOptions = new SelectableOptions();
  // Selected Years
  selectedStartYear: number;
  selectedEndYear: number;
  // Selected Output by options
  selectedSgmntLvl: any = 0;
  selectedGeography: string;
  selectedVehDetails: any = [];

  memberScenarioColumns: ColDef[] = VEHICLE_COLDEF_NO_FILTER
  memberVehicleScenarios: VehicleScenario[];
  compareMemberVehicleScenarios: VehicleScenario[];
  scenarioYearRange: number[] = [];
  fcorgs: string[];
  detailData: any[] = [];
  filteredData: any[] = [];
  vehDetailOptions: any = [
    {label: this.ARCH, value: this.ARCH},
    {label: this.MKTDIV, value: this.MKTDIV},
    {label: this.LBRAND, value: this.LBRAND},
    {label: this.PRGMCODE, value: this.PRGMCODE},
  ];


  constructor(public snackBar: FabMessageService,
              public readonly excelService: ExcelExportService,
              readonly vehicleScenarioService: VehicleScenarioService,
              readonly globalScenarioService: GlobalScenarioService,
              public stateService: StateService,
              public dialog: MatDialog,
              readonly router: Router) {
  }

  ngOnInit(): void {
    this.isIncludeTos = false;
    this.isIncludeCompareScenario = false;
    this.combinePhevIce = true;
    this.selectedScenarioType = 'fcorg';
  }

  /**
   * Opens the pop-up to select a vehicle scenario
   */
  openSelectWorkingScen(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiselect: false,
        title: 'Select Vehicle Scenario',
        validFcorgs: ['GMNA', 'GMSA', 'CHEV CAD EUROPE', 'GMI', 'GMGC', 'GMTVP']
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.selectedVehScenRow = res.selectedScenarios[0];
        this.compareScenario = this.selectedVehScenRow;
        this.resetAllSelectedFilter();
        this.getFiltersForMarketShareSegmentReport(this.selectedVehScenRow.vehScenId);
        this.getYearRangeForVehScen();
        this.selectedFcorgName = this.selectedVehScenRow.fcorg;
      }
    });
  }

  /**
   * Open the Select Compare Scenario Pop up component
   */
  openSelectCompareScen(): void {
    const dialogRef = this.dialog.open(VehicleScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        validFcorgs: [this.selectedVehScenRow.fcorg],
        title: 'Select Compare Scenario',
        isMultiselect: false,
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.compareScenario = res.selectedScenarios[0];
      }
    });
  }

  /**
   * Event handler for when a working global scenario is selected
   * @param data
   */
  onWorkingGlobalScenarioSelect(data: any): void {
    this.selectedGlblScenRow = data.globalScenario;
    this.memberVehicleScenarios = data.memberScenarios;
    this.resetAllSelectedFilter();
    this.getFiltersForMarketShareSegmentReport(this.selectedGlblScenRow.id);
    this.getYearRangeForGlobalScen();
  }

  /**
   * Event handler for when a compare global scenario is selected
   * @param data
   */
  onCompareGlobalScenarioSelect(data: any): void {
    this.selectedCompareGlblScenRow = data.globalScenario;
    this.compareMemberVehicleScenarios = data.memberScenarios;
  }

  /**
   * Gets the Year range for a global scenario
   */
  getYearRangeForGlobalScen(): void {
    this.globalScenarioService.getYearRange(this.selectedGlblScenRow.id, this.stateService.getUserGmid(), true).subscribe(
      (data: any[]) => {
        if (data && data.length > 0) {
          // If the default year range is a valid range of volumes in the scenario, then use the default year range. else use the year range values from the scenario
          const scenarioMinYear = data[0].MIN_YEAR;
          const scenarioMaxYear = data[0].MAX_YEAR;
          const {startYear, endYear} = getDefaultYearRange();
          if (startYear < scenarioMaxYear) {
            this.selectedStartYear = Math.max(scenarioMinYear, startYear);
            this.selectedEndYear = Math.min(endYear, scenarioMaxYear);
            this.scenarioYearRange = range(Math.min(scenarioMinYear, startYear), Math.max(endYear, scenarioMaxYear) + 1);
          } else {
            this.selectedStartYear = scenarioMinYear;
            this.selectedEndYear = scenarioMaxYear;
            this.scenarioYearRange = range(this.selectedStartYear, this.selectedEndYear + 1);
          }
        } else {
          this.throwNoVolumeDataError();
        }
      }, err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      });
  }

  /**
   * Gets the year range for a vehicle scenario
   */
  getYearRangeForVehScen(): void {
    this.vehicleScenarioService.getYearRange(this.selectedVehScenRow.vehScenId).subscribe(
      (data: any[]) => {
        if (data && data.length > 0) {
          // If the default year range is a valid range of volumes in the scenario, then use the default year range. else use the year range values from the scenario
          const scenarioMinYear = data[0].MIN_YEAR;
          const scenarioMaxYear = data[0].MAX_YEAR;
          const {startYear, endYear} = getDefaultYearRange();
          if (startYear < scenarioMaxYear) {
            this.selectedStartYear = Math.max(scenarioMinYear, startYear);
            this.selectedEndYear = Math.min(endYear, scenarioMaxYear);
            this.scenarioYearRange = range(Math.min(scenarioMinYear, startYear), Math.max(endYear, scenarioMaxYear) + 1);
          } else {
            this.selectedStartYear = scenarioMinYear;
            this.selectedEndYear = scenarioMaxYear;
            this.scenarioYearRange = range(this.selectedStartYear, this.selectedEndYear + 1);
          }
        } else {
          this.throwNoVolumeDataError();
        }
      }, err => {
        this.snackBar.displayError(err, DISMISS_MSG);
      });
  }

  /**
   * Get the Vehicle scenario details of a vehicle scenario or the vehicle scenarios attached to a global scenario, and then
   * initialize the filters.
   */
  getFiltersForMarketShareSegmentReport(scenarioId: number): void {
    this.showSpinner = true;
    const isGlobal = this.selectedGlblScenRow ? 1 : 0;
    this.vehicleScenarioService.getFiltersForMarketShareSegmentReport(this.stateService.getUserGmid(), scenarioId, isGlobal)
    .subscribe(data => {
      this.detailData = data;
      this.filteredData = data;
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
      this.initializeFilterOptions();
    });
  }

  /**
   * Initialize the filter options for all the filters
   */
  initializeFilterOptions(): void {
    this.regionOptions.options = this.setFilterOption(this.REGION);
    this.setCascadingFilterOptions(this.REGION);
  }

  /**
   * Function used to set the filter options for the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  setCascadingFilterOptions(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        this.subregionOptions.options = this.setFilterOption(this.SUBREGION);
      // eslint-disable-next-line
      case this.SUBREGION:
        this.plngMktOptions.options = this.setFilterOption(this.PLNGMKT);
      // eslint-disable-next-line
      case this.PLNGMKT:
        this.archOptions.options = this.setFilterOption(this.ARCH);
      // eslint-disable-next-line
      case this.ARCH:
        this.mktDivOptions.options = this.setFilterOption(this.MKTDIV);
      // eslint-disable-next-line
      case this.MKTDIV:
        this.lbrandOptions.options = this.setFilterOption(this.LBRAND);
      // eslint-disable-next-line
      case this.LBRAND:
        this.prgmCodeOptions.options = this.setFilterOption(this.PRGMCODE);
      // eslint-disable-next-line
      case this.PRGMCODE:
        this.tosOptions.options = this.setFilterOption(this.TOS);
    }
  }

  /**
   * Reset the selected options from the passed in filter + any of the cascaded filters below it
   * @param filterType the filter type (which filter it is)
   */
  cascadeResetSelectedFilter(filterType: string): void {
    switch (filterType) {
      case this.REGION:
        if (this.subregionOptions.selectedOptions.length > 0) {
          this.subregionOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.SUBREGION:
        if (this.plngMktOptions.selectedOptions.length > 0) {
          this.plngMktOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.PLNGMKT:
        if (this.archOptions.selectedOptions.length > 0) {
          this.archOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.ARCH:
        if (this.mktDivOptions.selectedOptions.length > 0) {
          this.mktDivOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.MKTDIV:
        if (this.lbrandOptions.selectedOptions.length > 0) {
          this.lbrandOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.LBRAND:
        if (this.prgmCodeOptions.selectedOptions.length > 0) {
          this.prgmCodeOptions.selectedOptions = [];
        }
      // eslint-disable-next-line
      case this.PRGMCODE:
        if (this.tosOptions.selectedOptions.length > 0) {
          this.tosOptions.selectedOptions = [];
        }
    }
  }

  /**
   * Function used to update the filtered data based off of the selected filters for a particular filter type
   * @param filterType the filter type (which filter it is)
   * @param selectedFilters list of filter values that were selected from the filter
   */
  applyFilter(filterType: string, selectedFilters: any): void {
    if (selectedFilters.length > 0) {
      this.filteredData = this.filteredData.filter(detailRecord => selectedFilters.includes(detailRecord[filterType].toUpperCase()));
    }
  }

  /**
   * Event handler whenever a filter changes
   * @param filterType the filter type (which filter it is)
   */
  onFilterChange(filterType: string): void {
    // Reset the filtered data to the original data
    this.filteredData = this.detailData;
    // Reset the selected filters for cascading filters below the filter that was passed in
    this.cascadeResetSelectedFilter(filterType);
    // Update the filtered data based off of which filters are selected
    this.applyFilter(this.REGION, this.regionOptions.selectedOptions);
    this.applyFilter(this.SUBREGION, this.subregionOptions.selectedOptions);
    this.applyFilter(this.PLNGMKT, this.plngMktOptions.selectedOptions);
    this.applyFilter(this.ARCH, this.archOptions.selectedOptions);
    this.applyFilter(this.MKTDIV, this.mktDivOptions.selectedOptions);
    this.applyFilter(this.LBRAND, this.lbrandOptions.selectedOptions);
    this.applyFilter(this.PRGMCODE, this.prgmCodeOptions.selectedOptions);
    // Update the filter options for cascading filters below the filter that was passed in
    this.setCascadingFilterOptions(filterType);
  }

  /**
   * Generate and export the report
   */
  onRunReport(): void {
    // Create Market Share Segment Report Request
    const isGlobalScen = this.selectedGlblScenRow !== undefined;
    const reportRequest = new MarketShareSegmentReportRequest();
    let compScenId = null;
    if (this.isIncludeCompareScenario) {
      compScenId = isGlobalScen ? this.selectedCompareGlblScenRow.id : this.compareScenario.vehScenId;
    }
    reportRequest.userId = this.stateService.getUserGmid();
    reportRequest.workingScenId = isGlobalScen ? this.selectedGlblScenRow.id : this.selectedVehScenRow.vehScenId;
    reportRequest.isGlobal = isGlobalScen ? 1 : 0;
    reportRequest.selectedRegionFilters = this.regionOptions.selectedOptions;
    reportRequest.selectedSubregionFilters = this.subregionOptions.selectedOptions;
    reportRequest.selectedPlngMktFilters = this.plngMktOptions.selectedOptions;
    reportRequest.selectedArchFilters = this.archOptions.selectedOptions;
    reportRequest.selectedMktDivFilters = this.mktDivOptions.selectedOptions;
    reportRequest.selectedLbrandFilters = this.lbrandOptions.selectedOptions;
    reportRequest.selectedPrgmCodeFilters = this.prgmCodeOptions.selectedOptions;
    reportRequest.selectedTosFilters = this.tosOptions.selectedOptions;
    reportRequest.startYear = this.selectedStartYear;
    reportRequest.endYear = this.selectedEndYear;
    reportRequest.segLvl = this.selectedSgmntLvl;
    reportRequest.geography = this.selectedGeography;
    reportRequest.vehDetailOutputs = this.selectedVehDetails;
    reportRequest.isIncludeTos = this.isIncludeTos ? 1 : 0;
    reportRequest.compareScenId = compScenId;
    // Only utilize the combine PHEV ICE flag if the segment level is Propulsion Segment
    reportRequest.combinePhevIce = this.selectedSgmntLvl === 3 ? this.combinePhevIce : false;

    this.getMarketShareSegmentReport(reportRequest);
  }

  /**
   * Get the data for the Market Share Segment Report and then export it
   * @param reportRequest the request object that contains the information for running the report
   */
  getMarketShareSegmentReport(reportRequest: MarketShareSegmentReportRequest): void {
    let reportData: any[];
    this.showSpinner = true;
    this.vehicleScenarioService.getMarketShareSegmentReport(reportRequest).subscribe(data => {
      reportData = data;
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      // Only export data if the report data that is returned back isn't empty
      if (reportData.length > 0) {
        this.exportMarketShareSegmentReport(reportData);
      } else {
        this.snackBar.displayMessage('There was no volume data found with the filter criteria provided.', DISMISS_MSG);
      }
      this.showSpinner = false;
    });
  }

  /**
   * Exports the market share segment report
   * @param reportData the report data to export
   */
  exportMarketShareSegmentReport(reportData: any): void {
    this.showSpinner = true;
    const currDate = new Date().toISOString();
    const exportRequest = new WorksheetExportRequest();
    const firstScenario = this.selectedGlblScenRow ?
      this.selectedGlblScenRow.id : this.selectedVehScenRow.vehScenId;
    const secondScenario = this.selectedGlblScenRow ?
      this.selectedCompareGlblScenRow?.id : this.compareScenario?.vehScenId
    const scenarioString = firstScenario + (this.isIncludeCompareScenario ? '-' + secondScenario : '');
    exportRequest.fileName = 'MarketShareSgmnt_' + scenarioString + "_" + currDate;
    exportRequest.data = new Map<string, any[]>();
    exportRequest.headers = new Map<string, string[]>();
    exportRequest.fieldNames = new Map<string, string[]>();
    exportRequest.numTypeFields = new Map<string, string[]>();
    exportRequest.percentTypeFields = new Map<string, string[]>();
    this.addCoverPageToExportRequest(exportRequest);
    this.addReportToExportRequest(exportRequest, reportData);

    // Call the Excel service to export
    this.excelService.transformToExcel(exportRequest).subscribe(
      (file: any) => {
        this.excelService.exportToExcelFromServer(file, exportRequest.fileName);
      },
      () => {
        this.snackBar.displayError(EXPORT_DATA_ERROR, DISMISS_MSG);
        this.showSpinner = false;
      }, () => {
        this.showSpinner = false;
      });
  }

  /**
   * Adds information about the cover page to a WorksheetExportRequest
   * @param exportRequest the worksheet export request to add to
   */
  addCoverPageToExportRequest(exportRequest: WorksheetExportRequest): void {
    const sheetName = 'Cover Page';
    const separator = ', ';
    const isGlobalScen = this.selectedGlblScenRow !== undefined;
    let compScenId = null;
    if (this.isIncludeCompareScenario) {
      compScenId = isGlobalScen ? this.selectedCompareGlblScenRow.id : this.compareScenario.vehScenId;
    }
    let compScenName = '';
    if (this.isIncludeCompareScenario) {
      compScenName = isGlobalScen ? this.selectedCompareGlblScenRow.globalScenarioName : this.compareScenario.vehScenName
    }
    exportRequest.data[sheetName] = [{
      SCEN_ID: isGlobalScen ? this.selectedGlblScenRow.id : this.selectedVehScenRow.vehScenId,
      SCEN_NAME: isGlobalScen ? this.selectedGlblScenRow.globalScenarioName :
        this.selectedVehScenRow.vehScenName,
      IND_SCEN_ID: isGlobalScen ? '' : this.selectedVehScenRow.industryScenario.id,
      IND_SCEN_NAME: isGlobalScen ? '' : this.selectedVehScenRow.industryScenario.industryScenarioName,
      MEMBER_SCENARIOS: isGlobalScen ? this.getMemberScenarioString() : '',
      COMP_SCEN_ID: compScenId,
      COMP_SCEN_NAME: compScenName,
      REGION: this.regionOptions.selectedOptions.join(separator),
      SUBREGION: this.subregionOptions.selectedOptions.join(separator),
      PLNGMKT: this.plngMktOptions.selectedOptions.join(separator),
      ARCH: this.archOptions.selectedOptions.join(separator),
      MKTDIV: this.mktDivOptions.selectedOptions.join(separator),
      LBRAND: this.lbrandOptions.selectedOptions.join(separator),
      PRGMCODE: this.prgmCodeOptions.selectedOptions.join(separator),
      TOS: this.tosOptions.selectedOptions.join(separator),
      SEG_LVL: this.selectedSgmntLvl,
      GEOGRAPHY: this.selectedGeography,
      VEH_DTL: this.selectedVehDetails.join(separator),
      INCLUDE_TOS: this.isIncludeTos ? 'Yes' : 'No',
    }];
    const headerData: string[] = ['SCEN_ID', 'SCEN_NAME', 'IND_SCEN_ID', 'IND_SCEN_NAME', 'MEMBER_SCENARIOS', 'COMP_SCEN_ID', 'COMP_SCEN_NAME',
      this.REGION, this.SUBREGION, this.PLNGMKT, this.ARCH, this.MKTDIV, this.LBRAND, this.PRGMCODE, this.TOS,
      'SEG_LVL', 'GEOGRAPHY', 'VEH_DTL', 'INCLUDE_TOS'];
    exportRequest.headers[sheetName] = headerData;
    exportRequest.fieldNames[sheetName] = headerData;
  }

  /**
   * Adds information about the market share report to a WorksheetExportRequest
   * @param exportRequest the worksheet export request to add to
   * @param reportData the data in the report
   */
  addReportToExportRequest(exportRequest: WorksheetExportRequest, reportData: any): void {
    const sheetName = 'MktShrRpt';
    exportRequest.data[sheetName] = reportData;
    const headerData = Object.keys(exportRequest.data[sheetName][0]);
    exportRequest.headers[sheetName] = headerData;
    exportRequest.fieldNames[sheetName] = headerData;
    // Set which fields will be numbers
    exportRequest.numTypeFields[sheetName] = ['GM_VEH_VOL', 'IND_VOL'];
    // Set which fields will be percent types
    exportRequest.percentTypeFields[sheetName] = ['MKT_SHARE'];
    // If the compare scenario is included, add the fields for the variance
    if (this.isIncludeCompareScenario) {
      exportRequest.numTypeFields[sheetName] = ['WORK_SCEN_GM_VEH_VOL', 'COMP_SCEN_GM_VOL', 'GM_VOL_VARIANCE', 'WORK_SCEN_IND_VOL', 'COMP_SCEN_IND_VOL', 'IND_VOL_VARIANCE'];
      // Set which fields will be percent types
      exportRequest.percentTypeFields[sheetName] = ['WORK_SCEN_MKT_SHARE', 'COMP_SCEN_MKT_SHARE', 'MKT_SHARE_VARIANCE'];
    }
  }

  /**
   * Helper function to convert the list of member scenarios of a Global scenario into a string
   */
  getMemberScenarioString(): string {
    let memberScenarioString = '';
    if (this.selectedGlblScenRow && this.memberVehicleScenarios.length > 0) {
      // Filter out the NON-GM Scenarios
      const filteredMemberVehicleScenarios = this.memberVehicleScenarios.filter(row =>
        !row.fcorg.includes('NONGM'));
      // Generate the member scenario string to return
      const memberScenarioStringList = filteredMemberVehicleScenarios.map(scenario => {
        return '{VEH_SCEN: ' + scenario.vehScenId + ', ' + scenario.vehScenName + '; IND_SCEN: '
          + scenario.industryScenario.id + ', ' + scenario.industryScenario.industryScenarioName + '}';
      });
      memberScenarioString = memberScenarioStringList.join(', ');
    }
    return memberScenarioString;
  }

  /**
   * Helper function to return back a list of available filter options for the specified filter
   * @param filterFieldName the name of the filter to set the filter options for
   */
  setFilterOption(filterFieldName: string): string[] {
    return [...new Set(this.filteredData
    .sort((a, b) => a[filterFieldName].toUpperCase() > b[filterFieldName].toUpperCase() ? 1 : -1)
    .map(detailRecord => detailRecord[filterFieldName].toUpperCase()))];
  }

  /**
   * Resets all the selected filters from the filter dropdowns
   */
  resetAllSelectedFilter(): void {
    this.selectedSgmntLvl = 0;
    this.regionOptions.selectedOptions = [];
    this.selectedVehDetails = [];
    this.cascadeResetSelectedFilter(this.REGION);
  }

  /**
   * Reset all the filter options from the filter dropdowns
   */
  resetAllFilterOptions(): void {
    this.regionOptions.options = [];
    this.subregionOptions.options = [];
    this.plngMktOptions.options = [];
    this.archOptions.options = [];
    this.mktDivOptions.options = [];
    this.lbrandOptions.options = [];
    this.prgmCodeOptions.options = [];
    this.tosOptions.options = [];
  }

  /**
   * Reset variables back to their default values
   */
  resetVariables(): void {
    this.selectedGlblScenRow = undefined;
    this.selectedCompareGlblScenRow = undefined;
    this.selectedVehScenRow = undefined;
    this.compareScenario = undefined;
    this.selectedFcorgName = undefined;
    this.selectedGeography = undefined;
    this.showSpinner = false;
    this.resetAllFilterOptions();
    this.resetAllSelectedFilter();
  }

  /**
   * Reset the year values for the report and throw an error indicating that no volume data was found
   */
  throwNoVolumeDataError(): void {
    this.selectedStartYear = undefined;
    this.selectedEndYear = undefined;
    this.scenarioYearRange = [];
    this.snackBar.displayError('Unable to get year range because there is no volume data.', DISMISS_MSG);
  }
}
